/**
 * 会员等级管理 — 17-level tier system (E1→S9)
 *
 * Full CRUD for all configurable level parameters:
 *   - Basic info (name, tier, sort)
 *   - Promotion requirements (entry_fee, direct_e2, small_market_perf, donation_uo, a5_in_small_market)
 *   - Weekly EP caps (weekly_ep_cap, weekly_ep_cap_sub)
 *   - Mentorship circles (circle-based percentages)
 *   - Promotion bonus (promotion_rp)
 *
 * GET  /api/admin/levels
 * GET  /api/admin/levels/:code
 * PATCH /api/admin/levels/:code
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Card,
  Col,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  message,
} from 'antd';
import { api, ApiError } from '../../api/client';
import { useAuthStore } from '../../store/auth';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface MentorCircle {
  from: number;
  to: number;
  pct: string;
}

interface LevelRow {
  code: string;
  name: string;
  tier: string;
  sort: number;
  entry_fee: string;
  direct_e2: number;
  small_market_perf: string;
  donation_uo: string;
  donation_title: string | null;
  a5_in_small_market: number;
  weekly_ep_cap: string;
  weekly_ep_cap_sub: string;
  mentor_circle_max: number;
  mentor_circles: MentorCircle[];
  promotion_rp: string;
  min_weight: string;
  bonus_pct: string;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

const TIER_COLORS: Record<string, string> = { E: 'green', A: 'blue', S: 'gold' };
const TIER_LABELS: Record<string, string> = { E: 'Entry', A: 'Advanced', S: 'Senior' };

function formatNum(v: string): string {
  const n = Number(v);
  if (isNaN(n) || n === 0) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return n.toString();
}

function circlesDisplay(circles: MentorCircle[]): string {
  if (!circles?.length) return '—';
  return circles
    .map((c) => `圈${c.from === c.to ? c.from : `${c.from}-${c.to}`}: ${(Number(c.pct) * 100).toFixed(0)}%`)
    .join(', ');
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function UserLevelsPage() {
  const qc = useQueryClient();
  const hasNode = useAuthStore((s) => s.hasNode);
  const canEdit = hasNode('platform.level.edit');
  const [edit, setEdit] = useState<LevelRow | null>(null);
  const [form] = Form.useForm();

  const { data, isFetching } = useQuery({
    queryKey: ['admin', 'levels'],
    queryFn: async () => {
      const res: any = await api.get('/api/admin/levels');
      return res as { rows: LevelRow[] };
    },
  });

  const save = useMutation({
    mutationFn: (payload: { code: string; body: Record<string, unknown> }) =>
      api.patch(`/api/admin/levels/${encodeURIComponent(payload.code)}`, payload.body),
    onSuccess: () => {
      message.success('已保存');
      setEdit(null);
      void qc.invalidateQueries({ queryKey: ['admin', 'levels'] });
    },
    onError: (e) => message.error(e instanceof ApiError ? e.message : '保存失败'),
  });

  const rows = data?.rows ?? [];
  const eTier = rows.filter((r) => r.tier === 'E');
  const aTier = rows.filter((r) => r.tier === 'A');
  const sTier = rows.filter((r) => r.tier === 'S');

  const openEdit = (r: LevelRow) => {
    setEdit(r);
    form.setFieldsValue({
      name: r.name,
      tier: r.tier,
      sort: r.sort,
      entry_fee: r.entry_fee,
      direct_e2: r.direct_e2,
      small_market_perf: r.small_market_perf,
      donation_uo: r.donation_uo,
      donation_title: r.donation_title ?? '',
      a5_in_small_market: r.a5_in_small_market,
      weekly_ep_cap: r.weekly_ep_cap,
      weekly_ep_cap_sub: r.weekly_ep_cap_sub,
      promotion_rp: r.promotion_rp,
      mentor_circles_raw: r.mentor_circles?.length
        ? JSON.stringify(r.mentor_circles, null, 2)
        : '[]',
    });
  };

  const handleSave = (values: any) => {
    if (!edit) return;
    const body: Record<string, unknown> = {
      name: values.name,
      tier: values.tier,
      sort: values.sort,
      entry_fee: values.entry_fee,
      direct_e2: values.direct_e2,
      small_market_perf: values.small_market_perf,
      donation_uo: values.donation_uo,
      donation_title: values.donation_title || null,
      a5_in_small_market: values.a5_in_small_market,
      weekly_ep_cap: values.weekly_ep_cap,
      weekly_ep_cap_sub: values.weekly_ep_cap_sub,
      promotion_rp: values.promotion_rp,
    };
    // Parse mentor circles JSON
    try {
      const circles = JSON.parse(values.mentor_circles_raw || '[]');
      body.mentor_circles = circles;
    } catch {
      message.error('辅导奖圈配置 JSON 格式错误');
      return;
    }
    save.mutate({ code: edit.code, body });
  };

  const columns = [
    { title: '代码', dataIndex: 'code', width: 60, fixed: 'left' as const },
    {
      title: '名称',
      dataIndex: 'name',
      width: 120,
      render: (v: string, r: LevelRow) => (
        <Space>
          <Tag color={TIER_COLORS[r.tier]}>{r.tier}</Tag>
          {v}
        </Space>
      ),
    },
    { title: '入金', dataIndex: 'entry_fee', width: 90, render: formatNum },
    { title: '直推E2', dataIndex: 'direct_e2', width: 70 },
    { title: '小区业绩', dataIndex: 'small_market_perf', width: 100, render: formatNum },
    { title: '捐赠ORB', dataIndex: 'donation_uo', width: 90, render: formatNum },
    { title: '小区A5', dataIndex: 'a5_in_small_market', width: 70 },
    { title: 'EP上限/周', dataIndex: 'weekly_ep_cap', width: 90, render: formatNum },
    { title: 'EP子账户', dataIndex: 'weekly_ep_cap_sub', width: 90, render: formatNum },
    {
      title: '辅导奖',
      key: 'mentor',
      width: 200,
      render: (_: unknown, r: LevelRow) => circlesDisplay(r.mentor_circles),
    },
    { title: '晋级奖RP', dataIndex: 'promotion_rp', width: 90, render: formatNum },
    { title: '排序', dataIndex: 'sort', width: 50 },
    {
      title: '操作',
      key: 'op',
      width: 70,
      fixed: 'right' as const,
      render: (_: unknown, r: LevelRow) =>
        canEdit ? (
          <Button type="link" size="small" onClick={() => openEdit(r)}>
            编辑
          </Button>
        ) : (
          '—'
        ),
    },
  ];

  const tierTable = (tierRows: LevelRow[], title: string) => (
    <Card size="small" title={title} style={{ marginBottom: 16 }}>
      <Table<LevelRow>
        loading={isFetching}
        size="small"
        bordered
        rowKey="code"
        dataSource={tierRows}
        pagination={false}
        scroll={{ x: 1200 }}
        columns={columns}
      />
    </Card>
  );

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>等级管理 (17级体系)</h2>
      <Descriptions size="small" bordered column={3} style={{ marginBottom: 16 }}>
        <Descriptions.Item label="E-tier (入门)">E1 一星 → E3 三星</Descriptions.Item>
        <Descriptions.Item label="A-tier (进阶)">A1 水星 → A5 木星</Descriptions.Item>
        <Descriptions.Item label="S-tier (大使)">S1 地月系 → S9 宇宙</Descriptions.Item>
      </Descriptions>

      <Tabs
        defaultActiveKey="all"
        items={[
          {
            key: 'all',
            label: `全部 (${rows.length})`,
            children: (
              <>
                {tierTable(eTier, 'E-tier 入门等级')}
                {tierTable(aTier, 'A-tier 进阶等级')}
                {tierTable(sTier, 'S-tier 大使等级')}
              </>
            ),
          },
          { key: 'E', label: `E-tier (${eTier.length})`, children: tierTable(eTier, 'E-tier 入门等级') },
          { key: 'A', label: `A-tier (${aTier.length})`, children: tierTable(aTier, 'A-tier 进阶等级') },
          { key: 'S', label: `S-tier (${sTier.length})`, children: tierTable(sTier, 'S-tier 大使等级') },
        ]}
      />

      {/* ─── Edit modal ─────────────────────────────────────────────── */}
      <Modal
        open={edit !== null}
        title={`编辑等级 ${edit?.code} (${edit?.name})`}
        width={720}
        onCancel={() => setEdit(null)}
        footer={null}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Tabs
            size="small"
            items={[
              {
                key: 'basic',
                label: '基本信息',
                children: (
                  <Row gutter={16}>
                    <Col span={8}>
                      <Form.Item name="name" label="名称" rules={[{ required: true }]}>
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item name="tier" label="类别">
                        <Select options={[
                          { value: 'E', label: 'E (入门)' },
                          { value: 'A', label: 'A (进阶)' },
                          { value: 'S', label: 'S (大使)' },
                        ]} />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item name="sort" label="排序">
                        <InputNumber min={0} max={999} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                  </Row>
                ),
              },
              {
                key: 'promotion',
                label: '晋级条件',
                children: (
                  <Row gutter={16}>
                    <Col span={8}>
                      <Form.Item name="entry_fee" label="入金门槛 USDT">
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item name="direct_e2" label="直推二星数">
                        <InputNumber min={0} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item name="small_market_perf" label="小区业绩 USDT">
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item name="donation_uo" label="累积捐赠ORB">
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item name="donation_title" label="捐赠头衔">
                        <Input placeholder="如: E3爱心志愿者" />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item name="a5_in_small_market" label="小区木星数 (S-tier)">
                        <InputNumber min={0} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                  </Row>
                ),
              },
              {
                key: 'ep',
                label: 'EP上限',
                children: (
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item name="weekly_ep_cap" label="每周EP上限 (标准)">
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="weekly_ep_cap_sub" label="每周EP上限 (100$子账户)">
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>
                ),
              },
              {
                key: 'mentor',
                label: '辅导奖',
                children: (
                  <>
                    <Form.Item
                      name="mentor_circles_raw"
                      label="辅导奖圈配置 (JSON)"
                      extra='格式: [{"from":1,"to":1,"pct":"0.40"},{"from":2,"to":5,"pct":"0.10"}]'
                    >
                      <Input.TextArea rows={5} />
                    </Form.Item>
                    <Form.Item name="promotion_rp" label="晋级奖 RP (S-tier一次性奖励)">
                      <Input />
                    </Form.Item>
                  </>
                ),
              },
            ]}
          />
          <Space style={{ marginTop: 16 }}>
            <Button type="primary" htmlType="submit" loading={save.isPending}>
              保存
            </Button>
            <Button onClick={() => setEdit(null)}>取消</Button>
          </Space>
        </Form>
      </Modal>
    </div>
  );
}
